import * as React from "react"
import { cn } from "cn"

function WidgetCard({ className, ...props }: React.ComponentProps<"article">) {
  return (
    <article
      data-slot="widget-card"
      className={cn("group/widget-card relative flex flex-col rounded-2xl bg-card text-card-foreground shadow-xs ring-1 ring-foreground/5", className)}
      {...props}
    />
  )
}

const WidgetCardHeader = React.forwardRef<HTMLElement, React.ComponentPropsWithoutRef<"header"> & { sticky?: boolean }>(
  function WidgetCardHeader({ sticky = false, className, ...props }, ref) {
    return (
      <header
        ref={ref}
        data-slot="widget-card-header"
        data-sticky={sticky || undefined}
        className={cn(
          "flex min-h-14 shrink-0 items-center gap-3 rounded-t-2xl py-2.5 border-b border-foreground/5 bg-card ps-5 pe-3 data-sticky:sticky data-sticky:top-(--widget-sticky-top,var(--app-header-height)) data-sticky:z-20 data-stuck:z-31 data-stuck:ring-1 data-stuck:ring-foreground/5 data-stuck:before:pointer-events-none data-stuck:before:absolute data-stuck:before:-top-px data-stuck:before:size-[calc(var(--radius-2xl)+1px)] data-stuck:before:content-[''] data-stuck:before:-left-px data-stuck:before:bg-[radial-gradient(circle_at_100%_100%,transparent_calc(var(--radius-2xl)+1px),var(--canvas)_calc(var(--radius-2xl)+1px))] data-stuck:after:pointer-events-none data-stuck:after:absolute data-stuck:after:-top-px data-stuck:after:size-[calc(var(--radius-2xl)+1px)] data-stuck:after:content-[''] data-stuck:after:-right-px data-stuck:after:bg-[radial-gradient(circle_at_0%_100%,transparent_calc(var(--radius-2xl)+1px),var(--canvas)_calc(var(--radius-2xl)+1px))]",
          className
        )}
        {...props}
      />
    )
  }
)

function WidgetCardHeading({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="widget-card-heading" className={cn("flex min-w-0 flex-1 flex-col", className)} {...props} />
}

function WidgetCardTitle({ className, ...props }: React.ComponentProps<"h3">) {
  return <h3 data-slot="widget-card-title" className={cn("typo-label-md flex min-w-0 items-center gap-2 text-greyscale-700 dark:text-greyscale-300", className)} {...props} />
}

function WidgetCardDescription({ className, ...props }: React.ComponentProps<"p">) {
  return <p data-slot="widget-card-description" className={cn("typo-caption truncate text-muted-foreground", className)} {...props} />
}

function WidgetCardActions({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="widget-card-actions" className={cn("flex shrink-0 items-center gap-1", className)} {...props} />
}

function WidgetCardBody({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="widget-card-body" className={cn("@container/widget flex flex-col gap-6 p-5 sm:p-6", className)} {...props} />
}

function WidgetCardFragment({ heading, className, children, ...props }: React.ComponentProps<"section"> & { heading?: string }) {
  return (
    <section data-slot="widget-card-fragment" className={cn("@container/fragment flex min-w-0 flex-col gap-3", className)} {...props}>
      {heading && <h4 className="typo-overline text-muted-foreground">{heading}</h4>}
      {children}
    </section>
  )
}

export {
  WidgetCard,
  WidgetCardActions,
  WidgetCardBody,
  WidgetCardDescription,
  WidgetCardFragment,
  WidgetCardHeader,
  WidgetCardHeading,
  WidgetCardTitle,
}
